//External libs
import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Subscription } from 'rxjs';

//Local libs
import Service from 'src/app/common/service';
import PreferencesModel from './preferences.model';

/**
 * @service PreferencesService
 * @description Service for managing user preferences within the Trading Community module.
 * This service allows the application to store and retrieve user-specific UI configurations,
 * such as preferred chart types or dashboard layouts.
 *
 * It enables a personalized experience by persisting settings across sessions.
 * The service interacts with the `/tradingcommunity/preferences/` backend endpoint.
 *
 * **Key Responsibilities:**
 * 1. **Preference Retrieval**: Fetching stored preferences for a user within a business context.
 * 2. **Preference Storage**: Saving or updating user preferences (e.g., chart visualization settings).
 * 3. **Resource Management**: Handling HTTP request lifecycles to prevent memory leaks.
 *
 * **Data Flow:**
 * - **Get**: Component requests preferences -> Service calls API -> Returns array of preferences.
 * - **Add**: Component sends preference data (chartType) -> Service posts to API.
 *
 * @feature 1. Retrieve user preferences.
 * @feature 2. Save/Update user preferences.
 * @feature 3. Robust management of concurrent HTTP requests.
 *
 * @architecture
 * Extends base Service<PreferencesModel> providing REST API integration.
 * Uses HttpClient for HTTP operations and manages subscriptions for cleanup.
 *
 * @service-endpoint /tradingcommunity/preferences/
 * @service-provider root (singleton, application-wide)
 *
 * @author AquaCloud Development Team
 * @version 1.0.0
 */
@Injectable({
  providedIn: 'root'
})
export class PreferencesService extends Service<PreferencesModel> {

  /**
   * @type {Array<Subscription>}
   * @description Array tracking all ongoing HTTP request subscriptions.
   * Used to maintain references to active Observables so they can be unsubscribed
   * when the service is destroyed or when cancellation is requested.
   * @default []
   * @side-effect Used to store subscription references for cancellation via cancelOnGoingHttpRequests()
   */
  public ongoingRequests: Subscription[] = [];

  /**
   * @method constructor
   * @description Service constructor with dependency injection of HttpClient.
   *
   * Initializes the service by calling the parent Service constructor with the
   * HttpClient instance and the specific API endpoint path (`/tradingcommunity/preferences/`).
   *
   * @param {HttpClient} httpClient - @service Angular HTTP client for making HTTP requests to the backend.
   * @returns {void}
   */
  constructor(public httpClient: HttpClient) {
    super(httpClient, '/tradingcommunity/preferences/');
  }

  /**
   * @method getPeferencesbyUserIDBusinessID
   * @async
   * @description Fetch preferences for a specific user and business.
   *
   * Note: The current implementation uses a static path structure `businessID//userID/`,
   * which implies the backend might infer context from the auth token or the path is a placeholder.
   *
   * @param {string} businessId - The unique identifier of the business.
   * @param {string} userId - The unique identifier of the user.
   * @returns {Promise<Array<PreferencesModel>>} Promise resolving to an array of preference models.
   * @throws {HttpErrorResponse} Rejects the promise if the HTTP request fails.
   * @side-effect
   * - Initiates an HTTP GET request.
   * - Pushes the subscription to `ongoingRequests`.
   *
   * @example
   * preferencesService.getPeferencesbyUserIDBusinessID('bus-1', 'user-2').then(prefs => {
   *   console.log('User Prefs:', prefs);
   * });
   */
  public getPeferencesbyUserIDBusinessID(businessId: string, userId: string) {
    return new Promise<Array<PreferencesModel>>((resolve, reject) => {
      const request = this.httpClient.get<Array<PreferencesModel>>(`${this.baseUrl}${this.serviceUrl}businessID//userID/`, this.httpOptions).subscribe(response => {
        resolve(response);
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }

  /**
   * @method addPreferences
   * @async
   * @description Add or update user preferences.
   *
   * Currently supports saving the `chartType` preference.
   *
   * @param {string} businessID - The business ID (passed but currently not used in payload).
   * @param {string} userID - The user ID (passed but currently not used in payload).
   * @param {string} chartType - The preferred chart type (e.g., 'bar', 'line').
   * @returns {Promise<Array<PreferencesModel>>} Promise resolving to the result of the operation.
   * @throws {HttpErrorResponse} Rejects the promise if the HTTP request fails.
   * @side-effect
   * - Initiates an HTTP POST request.
   * - Pushes the subscription to `ongoingRequests`.
   *
   * @example
   * preferencesService.addPreferences('bus-1', 'user-2', 'bar').then(res => {
   *   console.log('Preferences saved');
   * });
   */
  public addPreferences(businessID: string, userID: string, chartType: string) {
    return new Promise<Array<PreferencesModel>>((resolve, reject) => {
      const request = this.httpClient.post<Array<PreferencesModel>>(`${this.baseUrl}${this.serviceUrl}`, {
        chartType: chartType
      }, this.httpOptions).subscribe(response => {
        resolve(response);
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }

  //To Cancel the onGoing Http Requests
  /**
   * @method cancelOnGoingHttpRequests
   * @description Cancel all ongoing/pending HTTP requests to prevent memory leaks.
   *
   * This method iterates through the `ongoingRequests` array and calls `unsubscribe()`
   * on each subscription. This is typically called when a component using this service
   * is destroyed (ngOnDestroy) to ensure that no network requests continue in the background.
   *
   * @returns {void}
   * @side-effect
   * - Aborts all pending HTTP requests tracked in `ongoingRequests`.
   * - Resets `ongoingRequests` to an empty array.
   */
  public cancelOnGoingHttpRequests(): void {
    this.ongoingRequests.forEach((request) => {
      if (request && typeof request.unsubscribe === 'function') {
        request.unsubscribe();
      }
    });
    this.ongoingRequests = [];
  }
}
